import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Globe, Check } from 'lucide-react'
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_NAMES,
  type SupportedLanguage
} from '@/i18n/config'

interface LanguageSwitcherProps {
  /** 'compact' = header (mini-menú); 'full' = settings (lista de botones) */
  variant?: 'compact' | 'full'
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'compact' }) => {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Idioma actual normalizado al código base (es-MX → es)
  const current = (i18n.language?.split('-')[0] || 'es') as SupportedLanguage

  // Cerrar menú al hacer clic fuera (solo modo compact)
  useEffect(() => {
    if (variant !== 'compact') return
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [variant])

  const changeLanguage = (lang: SupportedLanguage) => {
    if (lang === current) {
      setOpen(false)
      return
    }
    i18n.changeLanguage(lang).then(() => {
      // Toast en el idioma NUEVO (t ya resuelve al idioma recién activado)
      toast.success(t('languageChanged', { lang: LANGUAGE_NAMES[lang] }))
    })
    setOpen(false)
  }

  // ── Variante FULL (Settings): lista de botones ──
  if (variant === 'full') {
    return (
      <div className="flex flex-wrap gap-3">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isActive = lang === current
          return (
            <button
              key={lang}
              onClick={() => changeLanguage(lang)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all border ${
                isActive
                  ? 'bg-blue-600 text-white border-blue-500'
                  : 'bg-white/10 text-white/70 border-white/20 hover:bg-white/20 hover:text-white'
              }`}
            >
              {isActive && <Check className="w-4 h-4" />}
              {LANGUAGE_NAMES[lang]}
            </button>
          )
        })}
      </div>
    )
  }

  // ── Variante COMPACT (header): botón + mini-menú ──
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
        title={t('language')}
        aria-label={t('language')}
      >
        <Globe size={18} />
        <span className="text-sm font-medium uppercase">{current}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isActive = lang === current
            return (
              <button
                key={lang}
                onClick={() => changeLanguage(lang)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-blue-500/20 text-blue-300'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                {LANGUAGE_NAMES[lang]}
                {isActive && <Check className="w-4 h-4" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
